import React from 'react';
import { createPortal } from 'react-dom';
import { GRID_CONFIG } from '../gridConfig';

interface FlowTooltipProps {
  show: boolean;
  content: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
  offset?: { x: number; y: number };
  className?: string;
}

/**
 * FlowTooltip - A React Flow compatible tooltip that portals to document body
 *
 * FEATURES:
 * - Portals to document.body to escape React Flow viewport clipping
 * - Uses centralized z-index from GRID_CONFIG to ensure proper layering
 * - Automatically positions relative to target element
 * - Handles viewport edge detection and repositioning
 *
 * UX FIXES:
 * - Appears above all React Flow edges and nodes
 * - Never gets cut off at viewport boundaries
 * - Consistent z-index hierarchy across all tooltips
 */
export function FlowTooltip({
  show,
  content,
  targetRef,
  offset = { x: 10, y: -10 },
  className = '',
}: FlowTooltipProps) {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!show || !targetRef.current) return;

    const updatePosition = () => {
      const target = targetRef.current;
      if (!target) return;

      const targetRect = target.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate initial position
      let x = targetRect.right + offset.x;
      let y = targetRect.top + offset.y;

      // Adjust if tooltip would go off right edge
      if (x + 300 > viewportWidth) {
        // Assume ~300px tooltip width
        x = targetRect.left - 300 - Math.abs(offset.x);
      }

      // Adjust if tooltip would go off bottom edge
      if (y + 100 > viewportHeight) {
        // Assume ~100px tooltip height
        y = targetRect.bottom - 100 - Math.abs(offset.y);
      }

      // Ensure tooltip doesn't go off left edge
      if (x < 10) {
        x = 10;
      }

      // Ensure tooltip doesn't go off top edge
      if (y < 10) {
        y = 10;
      }

      setPosition({ x, y });
    };

    updatePosition();

    // Update position on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show, offset, targetRef]);

  if (!show) return null;

  const tooltipContent = (
    <div
      className={`absolute bg-black text-white text-sm px-3 py-2 rounded shadow-lg pointer-events-none ${className}`}
      style={{
        left: position.x,
        top: position.y,
        zIndex: GRID_CONFIG.zIndex.tooltips,
        maxWidth: '300px',
        wordWrap: 'break-word',
      }}
    >
      {content}
    </div>
  );

  // Portal to document.body to escape any container clipping
  return createPortal(tooltipContent, document.body);
}
